import {
  AppointmentListingModel,
  AppointmentManagementListModel,
  ClientModel,
  ClientVerificationRequest,
  CommunicationPreferenceModel,
  IAppointmentListingModel,
  IAppointmentManagementListModel,
  IClientModel,
  IClientVerificationRequest,
  IMedicalHistoryModel,
  MedicalHistoryModel,
  IClinicAddNewClientRequest,
  ClientCommunicationStatModel,
} from '@common/models';
import { IAddClientEmailRequest } from '@common/models/requests';
import { http } from '@common/utils';
import { DateTime } from 'luxon';
import { trackPromise } from 'react-promise-tracker';
import { ServiceHelper } from './serviceHelper';
import { ServiceMethodOptions } from './types';
import {
  CustomerInteractionModel,
  ICustomerInteractionModel,
} from '@common/models/CustomerInteractionModel';
import { AppointmentStatus, VisitCompletionType } from '@common/models/Enums';

export class ClientService {
  // #region Private Variables
  private static baseUrl: string = '/api/client';
  private static publicUserHeader: any = {
    vpca: '351BD313-2C19-442B-BC87-C69356EB59EF',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  };
  // #endregion

  public static tags = {
    getClient: 'getClient',
    addClient: 'addClient',
    updateClient: 'updateClient',
    validateCustomer: 'validateCustomer',
    getClientMedicalHistoryForPet: 'getClientMedicalHistoryForPet',
    getAppointmentAndVisitData: 'getAppointmentAndVisitData',
    getAppointments: 'getAppointments',
    addEmailCommunication: 'addEmailCommunication',
    getInteractions: 'getInteractions',
    addInteraction: 'addInteraction',
    updateInteraction: 'updateInteraction',
    deleteInteraction: 'deleteVisitAttachment',
    saveCommunicationPreference: 'saveCommunicationPreference',
    addNewClientFromClinic: 'addNewClientFromClinic',
    getCommunicationStat: 'getCommunicationStat',
  };

  static getClient = (
    clientId: string,
    options?: ServiceMethodOptions,
  ): Promise<ClientModel> =>
    trackPromise(
      new Promise<ClientModel>((resolve, reject) => {
        http(`${this.baseUrl}/${clientId}`)
          .get<IClientModel>()
          .then((data) => resolve(new ClientModel(data)))
          .catch(reject);
      }),
      ServiceHelper.getTagForOptions(this.tags.getClient, options),
    );

  static addClient = (
    client: ClientModel,
    options?: ServiceMethodOptions,
  ): Promise<ClientModel> =>
    trackPromise(
      new Promise<ClientModel>((resolve, reject) => {
        http(`${this.baseUrl}/hospital/add`)
          .post<IClientModel>(client)
          .then((data) => resolve(new ClientModel(data)))
          .catch(reject);
      }),
      ServiceHelper.getTagForOptions(this.tags.addClient, options),
    );

  static updateClient = (
    client: ClientModel,
    options?: ServiceMethodOptions,
  ): Promise<ClientModel> =>
    trackPromise(
      new Promise<ClientModel>((resolve, reject) => {
        http(`${this.baseUrl}/hospital/update`)
          .post<IClientModel>(client)
          .then((data) => resolve(new ClientModel(data)))
          .catch(reject);
      }),
      ServiceHelper.getTagForOptions(this.tags.updateClient, options),
    );

  /**
   * Validates User that supplys email or phone number
   * @param data Contains Email and/or Phone
   * @returns
   */
  static validateCustomer = (
    data: IClientVerificationRequest,
    options?: ServiceMethodOptions,
  ): Promise<ClientVerificationRequest> =>
    trackPromise(
      new Promise<IClientVerificationRequest>((resolve, reject) => {
        http(`${this.baseUrl}/verify/`)
          .mergeOptions({ headers: this.publicUserHeader })
          .post<IClientVerificationRequest>(data)
          .then((response) => {
            ServiceHelper.showSuccess('Verification Sent', options);
            resolve(new ClientVerificationRequest(response));
          })
          .catch((err) => {
            ServiceHelper.showError('Failed to validate.', options);
            reject(err);
          });
      }),
      ServiceHelper.getTagForOptions(this.tags.validateCustomer, options),
    );

  static getClientMedicalHistoryForPet = (
    clientId: number,
    petId: number,
    options?: ServiceMethodOptions,
  ): Promise<MedicalHistoryModel[]> =>
    trackPromise(
      new Promise<MedicalHistoryModel[]>((resolve, reject) => {
        http(
          `${this.baseUrl}/medicalhistory/${clientId}?includeFinalizedAndPaidVisits=true`,
        )
          .get<IMedicalHistoryModel[]>()
          .then((medicalHistories) => {
            const medicalHistoryClasses = medicalHistories
            .filter(
              (x) =>
                x.petId === petId &&
                (
                  x.isDiagnosis ||
                  x.isComplete ||
                  x.isFinalized ||
                  x.isPaid ||
                  VisitCompletionType.visitIsAborted?.(x.visitCompletionTypeId)
                ))
              .map((medicalHistory) => new MedicalHistoryModel(medicalHistory));
            resolve(medicalHistoryClasses);
          })
          .catch(() => {
            ServiceHelper.showError('Failed to fetch medical history', options);
            reject();
          });
      }),
      ServiceHelper.getTagForOptions(
        this.tags.getClientMedicalHistoryForPet,
        options,
      ),
    );

  /**
   * Gets appointment and visit data
   * @param publicId Public Id
   * @returns
   */
  static getAppointmentAndVisitData = (
    publicId: String,
    options?: ServiceMethodOptions,
  ): Promise<AppointmentManagementListModel[]> =>
    trackPromise(
      new Promise<AppointmentManagementListModel[]>((resolve, reject) => {
        http(`${this.baseUrl}/getappointmentandvisitdata/${publicId}`)
          .mergeOptions({ headers: this.publicUserHeader })
          .post<IAppointmentManagementListModel[]>({})
          .then((response) => {
            const apptMgtLstArray = response.map(
              (apptMgtLst) => new AppointmentManagementListModel(apptMgtLst),
            );
            resolve(apptMgtLstArray);
          })
          .catch((err) => {
            reject(err);
          });
      }),
      ServiceHelper.getTagForOptions(
        this.tags.getAppointmentAndVisitData,
        options,
      ),
    );

  static getAppointments = (
    clientId: number,
    options?: ServiceMethodOptions,
    startOfDayAtHospitalInUTCShift?: DateTime,
  ): Promise<AppointmentListingModel[]> =>
    trackPromise(
      new Promise<AppointmentListingModel[]>((resolve, reject) => {
        const url =
          startOfDayAtHospitalInUTCShift !== undefined
            ? `${this.baseUrl}/${clientId}/appointments`
            : `${this.baseUrl}/${clientId}/appointments?startDate=${DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISODate()}`;
        http(url)
          .get<IAppointmentListingModel[]>()
          .then((appts) => {
            ServiceHelper.showSuccess('Appointments Loaded!', options);
            const appointments = appts.map(
              (apt) => new AppointmentListingModel(apt),
            );
            resolve(
              startOfDayAtHospitalInUTCShift
                ? ClientService.filterAppointments(
                    appointments,
                    startOfDayAtHospitalInUTCShift,
                  )
                : appointments,
            );
          })
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to load client appointments.',
              options,
            );
            reject(err);
          });
      }),
      ServiceHelper.getTagForOptions(this.tags.getAppointments, options),
    );

  static addEmailCommunication = (
    emailRequest: IAddClientEmailRequest,
    options?: ServiceMethodOptions,
  ): Promise<ClientModel> =>
    trackPromise(
      new Promise<ClientModel>((resolve, reject) => {
        http(`${this.baseUrl}/addEmailCommunication`)
          .post<IClientModel>(emailRequest)
          .then((data) => resolve(new ClientModel(data)))
          .catch(reject);
      }),
      ServiceHelper.getTagForOptions(this.tags.addEmailCommunication, options),
    );

  static getClientInteractions = (clientId?: number) => {
    return trackPromise(
      http(`${this.baseUrl}/interactions/${clientId}`)
        .get<ICustomerInteractionModel[]>()
        .then((json) =>
          json.map(
            (customerInteraction) =>
              new CustomerInteractionModel(customerInteraction),
          ),
        ),
      this.tags.getInteractions,
    );
  };

  static addCustomerInteraction = (
    customerInteraction: Partial<CustomerInteractionModel>,
    options?: ServiceMethodOptions,
  ) =>
    trackPromise(
      http(`${this.baseUrl}/interactions/add`).post(customerInteraction),
      ServiceHelper.getTagForOptions(this.tags.addInteraction, options),
    );

  static updateCustomerInteraction = (
    customerInteraction: Partial<CustomerInteractionModel>,
    options?: ServiceMethodOptions,
  ) =>
    trackPromise(
      http(`${this.baseUrl}/interactions/update`).post(customerInteraction),
      ServiceHelper.getTagForOptions(this.tags.updateInteraction, options),
    );

  static deleteCustomerInteraction = (
    customerInteractionId: number,
    options?: ServiceMethodOptions,
  ) =>
    trackPromise(
      http(
        `${this.baseUrl}/interactions/delete/${customerInteractionId}`,
      ).delete(),
      ServiceHelper.getTagForOptions(this.tags.deleteInteraction, options),
    );

  static saveCommunicationPreference = (
    publicId: String,
    data: CommunicationPreferenceModel,
    options?: ServiceMethodOptions,
  ): Promise<void> =>
    trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/saveCommunicationPreference/${publicId}`)
          .mergeOptions({ headers: this.publicUserHeader })
          .post<CommunicationPreferenceModel>({
            canSMS: data.canSMS,
            canCall: data.canCall,
            phoneNumber: data.phoneNumber,
          })
          .then(() => resolve())
          .catch(reject);
      }),
      ServiceHelper.getTagForOptions(
        this.tags.saveCommunicationPreference,
        options,
      ),
    );

  static filterAppointments = (
    appointments: AppointmentListingModel[],
    startOfDayAtHospitalInUTCShift: DateTime,
  ) =>
    appointments.filter(
      (apt) =>
        apt.appointmentStatusId === AppointmentStatus.NoShow ||
        DateTime.fromISO(apt.startDate, { zone: 'UTC' }) >
          startOfDayAtHospitalInUTCShift,
    );

  static addNewClientFromClinic = (
    client: IClinicAddNewClientRequest,
    options?: ServiceMethodOptions,
  ): Promise<ClientModel> =>
    trackPromise(
      new Promise<ClientModel>((resolve, reject) => {
        http(`${this.baseUrl}/addfromclinic`)
          .post<IClientModel>(client)
          .then((data) => resolve(new ClientModel(data)))
          .then(() => ServiceHelper.showSuccess(`New Client Added!`, options))
          .catch((err) => {
            ServiceHelper.showError('Failed to Add Client.', options);
            reject(err);
          });
      }),
      ServiceHelper.getTagForOptions(this.tags.addNewClientFromClinic, options),
    );

  static getCommunicationStats = (
    clientId: number,
  ): Promise<ClientCommunicationStatModel> => {
    return trackPromise(
      new Promise<ClientCommunicationStatModel>((resolve, reject) => {
        http(`${this.baseUrl}/${clientId}/communicationstats`)
          .get<ClientCommunicationStatModel>()
          .then((data) => resolve(new ClientCommunicationStatModel(data)))
          .catch(() => reject());
      }),
      this.tags.getCommunicationStat,
    );
  };
}
